import { format } from "date-fns";
import {
  MY_CARDS_PAGE_SIZE,
  MY_CARDS_QUERY_KEY,
  myCardsKeys,
  type CardArtMeta,
  type FavoriteItem,
  type GiftItem,
  type InboxRow,
  type OutboxRow,
  type OutboxStatus,
  type SignedRecipient,
  type SignedRow,
  type SignerEntry,
} from "@shared/my-cards-data";
import { api, edenFetch } from "@/lib/eden";
import { queryClient } from "@/lib/queryClient";

/**
 * My Cards data plumbing (PRD 03 §6.3 / §8.2). The row shapes, query keys, page
 * size, and the short date formatter live in `@shared/my-cards-data` (shared with
 * the mobile My Cards tab); this module keeps the web-only fetchers, the legacy
 * tab/route vocabulary, and the gift/photo Library shapes.
 *
 * The page keeps the legacy tab set (`/mycards/:type` — upcoming / sent / signed
 * / received / drafts / favorites / photo / gift) mapped onto the new Inbox /
 * Outbox / Library API: received → inbox; upcoming, sent, drafts, signed →
 * outbox views; favorites, gift, photo → library views.
 */

export {
  MY_CARDS_PAGE_SIZE,
  MY_CARDS_QUERY_KEY,
  myCardsKeys,
  formatCardDateTimeShort,
  artAspect,
} from "@shared/my-cards-data";
export type {
  CardArtMeta,
  InboxRow,
  OutboxRow,
  OutboxStatus,
  SignedRecipient,
  SignedRow,
  SignerEntry,
  FavoriteItem,
  GiftStatus,
  GiftAttachment,
  GiftItem,
} from "@shared/my-cards-data";

export const MY_CARDS_TABS = [
  "upcoming",
  "sent",
  "signed",
  "received",
  "drafts",
  "favorites",
  "photo",
  "gift",
] as const;

export type MyCardsTab = (typeof MY_CARDS_TABS)[number];

export function isMyCardsTab(value: unknown): value is MyCardsTab {
  return typeof value === "string" && (MY_CARDS_TABS as readonly string[]).includes(value);
}

/** The tabs rendered as the legacy sent-cards table (not the browser/gift grids). */
export type MyCardsListTab = Exclude<MyCardsTab, "favorites" | "photo" | "gift">;

/** A member's photo (custom) card send past the draft stage — always has its snapshot. */
export interface PhotoItem extends CardArtMeta {
  card_send_id: string;
  status: OutboxStatus;
  snapshot_image_id: string | null;
  snapshot_url: string | null;
  read_slug: string | null;
  updated_at: string;
  /** BIGINT — serialized as a string by the API (no int8 parser). */
  card_no: string;
  slug: string;
  card_name: string;
}

export function invalidateMyCards(): void {
  void queryClient.invalidateQueries({ queryKey: [MY_CARDS_QUERY_KEY] });
}

const pageQuery = (page: number) => ({
  limit: MY_CARDS_PAGE_SIZE,
  offset: page * MY_CARDS_PAGE_SIZE,
});

export const fetchInbox = (page: number, onlyHidden = false) =>
  edenFetch(
    api.api.user["my-cards"].inbox.get({
      query: { ...pageQuery(page), ...(onlyHidden ? { onlyHidden: true } : {}) },
    }),
  ) as Promise<{ cards: InboxRow[]; total?: number }>;

/** The contact a `?contact_id=` outbox view is scoped to (legacy "Cards Sent" link). */
export interface OutboxContact {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export const fetchOutbox = (
  view: "sent" | "upcoming" | "drafts",
  page: number,
  contactId?: string | null,
) =>
  edenFetch(
    api.api.user["my-cards"].outbox.get({
      query: { view, ...pageQuery(page), ...(contactId ? { contactId } : {}) },
    }),
  ) as Promise<{ cards: OutboxRow[]; contact?: OutboxContact | null; total?: number }>;

/** One of the member's own sends, any status — by id (uuid or legacy numeric) or read_slug.
 *  The email-link landings: `?card_send_id=` (pinned row) and `/copy/read/<slug>`. */
export interface OutboxSingleBy {
  cardSendId?: string;
  readSlug?: string;
}
export const myCardsSingleKey = (by: OutboxSingleBy) =>
  [MY_CARDS_QUERY_KEY, "outbox", "single", by.cardSendId ?? null, by.readSlug ?? null] as const;
export const fetchOutboxSingle = (by: OutboxSingleBy) =>
  edenFetch(
    api.api.user["my-cards"].outbox.get({ query: { view: "sent", limit: 1, ...by } }),
  ) as Promise<{ cards: OutboxRow[] }>;

export const fetchSigned = (page: number) =>
  edenFetch(
    api.api.user["my-cards"].outbox.get({ query: { view: "signed", ...pageQuery(page) } }),
  ) as Promise<{ cards: SignedRow[]; total?: number }>;

export const fetchFavorites = () =>
  edenFetch(
    api.api.user["my-cards"].library.get({ query: { view: "favorites", limit: 100 } }),
  ) as Promise<{ items: FavoriteItem[] }>;

export const fetchGiftCards = () =>
  edenFetch(api.api.me["gift-cards"].get({ query: { limit: 100 } })) as Promise<{
    items: GiftItem[];
  }>;

/**
 * Reveal the redemption URL for a gift the member OWNS so they can share it directly (legacy
 * "Share Redeem Link"). A deliberate single-gift POST — ownership-checked + audited — so the
 * list response never carries spendable value (PRD 10 §12). 409s until the gift is issued.
 */
export const revealRedeemLink = (giftCardId: string) =>
  edenFetch(api.api.me["gift-cards"]({ id: giftCardId })["redeem-link"].post()) as Promise<{
    redemptionUrl: string;
  }>;

/**
 * Re-send the gift-card redemption email to its recipient (legacy "Send Gift Card Only").
 * 15-minute cooldown server-side; `already_sent` means the cooldown swallowed it.
 */
export const resendGiftCard = (giftCardId: string) =>
  edenFetch(api.api.me["gift-cards"]({ id: giftCardId }).resend.post()) as Promise<{
    ok: true;
    action: "sent" | "already_sent";
  }>;

export const fetchPhotoLibrary = () =>
  edenFetch(
    api.api.user["my-cards"].library.get({ query: { view: "photo", limit: 100 } }),
  ) as Promise<{ items: PhotoItem[] }>;

/**
 * Legacy pretty date: "March 29th, 2026 at 08:00 am" (hour zero-padded), via
 * date-fns like the reminders pages. Accepts a full ISO timestamp (rendered in
 * the viewer's zone) or a zoneless wall-clock string ("2026-07-25T08:00", which
 * `new Date` parses as local — i.e. rendered verbatim).
 */
export function formatCardDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "MMMM do, yyyy 'at' hh:mm aaa");
}
